import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon, 
  CheckCircle2, 
  Send,
  HeartPulse,
  Microscope,
  Activity,
  Stethoscope,
  Syringe,
  Brain,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { id: 'cardiovascular', label: 'Cardiovascular', icon: HeartPulse },
  { id: 'cancer', label: 'Cancer Detection', icon: Microscope },
  { id: 'diabetes', label: 'Diabetes', icon: Activity },
  { id: 'wellness', label: 'Wellness', icon: Stethoscope },
  { id: 'vaccination', label: 'Vaccination', icon: Syringe },
  { id: 'mental', label: 'Mental Health', icon: Brain },
];

const employeeOptions = [
  { value: '1-50', label: '1-50' },
  { value: '51-200', label: '51-200' },
  { value: '201-500', label: '201-500' },
  { value: '501-1000', label: '501-1000' },
  { value: '1000+', label: '1000+' },
];

export const RequestScreening = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    employee_count: '',
    preferred_date: null,
    location: '',
    services: [],
    additional_info: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter((s) => s !== serviceId)
        : [...prev.services, serviceId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.services.length === 0) {
      toast.error('Please select at least one service.');
      return;
    }

    if (!formData.employee_count) {
      toast.error('Please select the number of employees.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, 'yyyy-MM-dd') : null,
      };
      await axios.post(`${API}/screening-requests`, payload);
      setIsSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="request-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="overline mb-6 block">Request Screening</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Schedule your<br />
              <span className="gradient-text">health screening</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed">
              Fill out the form and our team will contact you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-24" data-testid="request-form-section">
        <div className="container mx-auto px-6 max-w-4xl">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-surface rounded-3xl p-10 md:p-16 text-center"
              data-testid="success-message"
            >
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
                Request Submitted!
              </h2>
              <p className="text-zinc-500 text-lg mb-8 max-w-md mx-auto">
                Our team will review your request and contact you within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" data-testid="back-home-btn">
                  <Button variant="outline" className="rounded-full px-8 py-6 border-white/20 text-white hover:bg-white/5">
                    Back to Home
                  </Button>
                </Link>
                <Link to="/services" data-testid="view-services-btn">
                  <Button className="rounded-full px-8 py-6 bg-emerald-500 hover:bg-emerald-400 text-black">
                    View Services
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-surface rounded-3xl p-8 md:p-10"
            >
              <h2 className="text-2xl font-bold text-white mb-8 font-['Outfit']">Company Information</h2>

              <form onSubmit={handleSubmit} className="space-y-8" data-testid="screening-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company_name" className="text-sm text-zinc-400 mb-2 block">Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      placeholder="Acme Corporation"
                      className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_name" className="text-sm text-zinc-400 mb-2 block">Contact Person *</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                      data-testid="contact-name-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-sm text-zinc-400 mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@acme.com"
                      className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm text-zinc-400 mb-2 block">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(555) 123-4567"
                      className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm text-zinc-400 mb-2 block">Employees *</Label>
                    <Select
                      value={formData.employee_count}
                      onValueChange={(value) => setFormData({ ...formData, employee_count: value })}
                    >
                      <SelectTrigger className="bg-zinc-900 border-white/10 text-white focus:border-emerald-500 rounded-lg h-10" data-testid="employee-count-select">
                        <SelectValue placeholder="Select count" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10 z-50">
                        {employeeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value} className="text-white hover:bg-white/5" data-testid={`employee-option-${option.value}`}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-zinc-400 mb-2 block">Preferred Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-zinc-900 border-white/10 text-white hover:bg-zinc-800 rounded-lg"
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                          {formData.preferred_date ? (
                            format(formData.preferred_date, 'PPP')
                          ) : (
                            <span className="text-zinc-600">Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-white/10" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.preferred_date}
                          onSelect={(date) => setFormData({ ...formData, preferred_date: date })}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="bg-zinc-900"
                          data-testid="calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label htmlFor="location" className="text-sm text-zinc-400 mb-2 block">Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    placeholder="123 Main St, City, State, ZIP"
                    className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                    data-testid="location-input"
                  />
                </div>

                {/* Services */}
                <div>
                  <Label className="text-sm text-zinc-400 mb-4 block">Select Services *</Label>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {serviceOptions.map((service) => (
                      <label
                        key={service.id}
                        htmlFor={`service-checkbox-${service.id}`}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all select-none ${
                          formData.services.includes(service.id)
                            ? 'border-emerald-500/50 bg-emerald-500/10'
                            : 'border-white/10 hover:border-white/20 bg-zinc-900/50'
                        }`}
                        data-testid={`service-${service.id}`}
                      >
                        <Checkbox
                          id={`service-checkbox-${service.id}`}
                          checked={formData.services.includes(service.id)}
                          onCheckedChange={() => handleServiceToggle(service.id)}
                          className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        />
                        <service.icon className={`w-4 h-4 pointer-events-none ${
                          formData.services.includes(service.id) ? 'text-emerald-500' : 'text-zinc-500'
                        }`} />
                        <span className={`text-sm font-medium pointer-events-none ${
                          formData.services.includes(service.id) ? 'text-white' : 'text-zinc-400'
                        }`}>
                          {service.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="additional_info" className="text-sm text-zinc-400 mb-2 block">Additional Information</Label>
                  <Textarea
                    id="additional_info"
                    name="additional_info"
                    value={formData.additional_info}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Any specific requirements or questions..."
                    className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg resize-none"
                    data-testid="additional-info-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full py-6 bg-emerald-500 hover:bg-emerald-400 text-black font-medium text-lg transition-all duration-200"
                  data-testid="submit-request-btn"
                >
                  {isSubmitting ? 'Submitting...' : (
                    <>
                      Submit Request
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 border-t border-white/5" data-testid="request-trust">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
            {[
              { value: '24hr', label: 'Response' },
              { value: 'Free', label: 'Consultation' },
              { value: '500+', label: 'Clients' },
              { value: '98%', label: 'Satisfaction' },
            ].map((item, index) => (
              <div key={item.label} data-testid={`trust-stat-${index}`}>
                <p className="text-xl font-bold text-emerald-500 font-['JetBrains_Mono']">{item.value}</p>
                <p className="text-zinc-600 text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
